import React, {useEffect, useMemo, useState} from 'react';
import {Col, Popconfirm, Row, Space} from 'antd';
import {CalculatorOutlined, EditOutlined} from '@ant-design/icons';
import moment from 'moment';

// Store
import {useAppDispatch} from '../../../store/store';
import {useSelector} from 'react-redux';
import * as allContainerShipmentSelector from '../../../store/redux/selectors/containerShipmentSelectors';
import {getParams, recalculateShipmentQuantities} from '../../../store/redux/actions/containerShipmentActions';

// Types
import {PaginationType} from '../../../types/sharedTypes';
import { ContainerShipmentType } from '../../../types/containerShipmentTypes';

// Components and Subcomponents
import TableComponent from '../../subComponents/TableComponent';
import ContainerShipmentFormModal from './ContainerShipmentFormModal';
import FiltersComponent from '../../subComponents/FiltersComponent';

// Functions and Constants
import {getFilterParameters, getTableColumns} from '../../../utils/componentsUtils';
import {MODAL_ACTIONS} from '../../../utils/constants';


const ContainerShipmentTable = () => {
  const [paginationData, setPaginationData] = useState<PaginationType>({
    page: 1,
    size: 10,
    filters: {}
  });
  const dispatch = useAppDispatch();
  const { rows, count, size, page } = useSelector(allContainerShipmentSelector.allContainerShipmentSelector);
  const { shipmentStateParams, timeStateParams } = useSelector(allContainerShipmentSelector.containerShipmentParamsSelector);
  const [shipmentToEdit, setShipmentToEdit] = useState<ContainerShipmentType | null>();
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [formModalAction, setModalAction] = useState('');
  const [list, setList] = useState<ContainerShipmentType[]>([]);
  const filterParameters = useMemo(() => {
    const params = getFilterParameters('containerShipment');

    return params.map((filter) => {
      if (filter.fieldName === 'status') {
        return {
          ...filter,
          list: shipmentStateParams
        };
      }

      if (filter.fieldName === 'isOnTime') {
        return {
          ...filter,
          list: timeStateParams
        };
      }

      return filter;
    });
  }, [shipmentStateParams, timeStateParams]);

  useEffect(() => {
    dispatch(getParams(paginationData));
  }, [paginationData]);

  useEffect(() => {
    const newList: ContainerShipmentType[] = rows.flatMap((containerShipment) => [
      {
        ...containerShipment,
        statusName: shipmentStateParams.find((status) => status.id.toString() == containerShipment.status.toString())?.value?.toString(),
      }
    ]);

    setList(newList);
  }, [shipmentStateParams, rows]);

  const handleFormModal = (visible: boolean, action?: string ,shipmentId?: string) => {
    if(visible && (action !== MODAL_ACTIONS.create)){
      const containerShipmentToEdit: ContainerShipmentType = JSON.parse(JSON.stringify(rows.find(shipment => shipment.shipmentId === shipmentId)));
      containerShipmentToEdit.eta = moment(containerShipmentToEdit?.eta);
      containerShipmentToEdit.ata = moment(containerShipmentToEdit?.ata);
      containerShipmentToEdit.ataContecar = moment(containerShipmentToEdit?.ataContecar);
      containerShipmentToEdit.unloadingDeadline = moment(containerShipmentToEdit?.unloadingDeadline);
      containerShipmentToEdit.scheduledUnloadingDate = moment(containerShipmentToEdit?.scheduledUnloadingDate);
      containerShipmentToEdit.unloadingDate = moment(containerShipmentToEdit?.unloadingDate);
      
      setShipmentToEdit(containerShipmentToEdit);
    } else {
      setShipmentToEdit(null);
    }

    setModalAction(action ?? '')
    setFormModalVisible(visible);
  }

  const actionButtons = (shipmentId: string) => {
    return (
      <Space>
        <EditOutlined className={'cursor-pointer hover:text-blue-dkt-400'}
                      onClick={() => handleFormModal(true, MODAL_ACTIONS.edit, shipmentId)} />
        <Popconfirm
          title="¿Está seguro que desea recalcular las cantidades de contenedores para este embarque?"
          onConfirm={() => {
            dispatch(recalculateShipmentQuantities(shipmentId, paginationData));
          }}
        >
          <CalculatorOutlined className={'cursor-pointer hover:text-blue-dkt-400'} />
        </Popconfirm>
      </Space>
    )
  };

  return (
    <>
      <Row>
        <Col xl={16} lg={18} md={16} xs={24}>
          <FiltersComponent filterParameters={filterParameters}
                            onChangeFilters={( value ) => setPaginationData({ page, size, filters: value })} />
        </Col>
      </Row>
      <TableComponent pageSize={size} currentPage={page} total={count}
                      filters={paginationData.filters ?? {}}
                      dataSource={list} scrollX={2500} rowKey={'shipmentId'}
                      rowClassName={(record: ContainerShipmentType) => !record.isOnTime ? 'table-row-danger' : ''}
                      columns={getTableColumns('containerShipments', actionButtons)}
                      onChangePage={setPaginationData} />
      {formModalVisible &&
        <ContainerShipmentFormModal modalAction={formModalAction} containerShipment={shipmentToEdit ?? null} onToggleModal={handleFormModal} />
      }
    </>
  );
}

export default ContainerShipmentTable;